import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { WebApiModel } from '../../../models/web-api.model';
import { ErrorHandlingService } from '../../../services/error-handling.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { ProfileService } from '../../../services/profile.service';
import { RouterHelperService } from '../../../services/router-helper.service';
import { SeoService } from '../../../services/seo.service';
import { SettingsService } from '../../../services/settings.service';
import { StateService } from '../../../services/state.service';
import { StateRxService } from '../../../services/staterx.service';
import { UiService } from '../../../services/ui.service';
import { FootnoteService } from '../../footnotes/footnote.service';
import { PopupComponent } from '../../popup/popup.component';

@Component({
  selector: 'hcf-person-profile',
  templateUrl: './person-profile.component.html',
  styleUrls: ['./person-profile.component.scss'],
  standalone: false,
})
export class PersonProfileComponent implements OnInit, OnDestroy {
  person: WebApiModel.Person;
  mapCenterLatitude: number;
  mapCenterLongitude: number;
  name: string;
  personServiceCategoryNamesByPlace: string[] = [];
  mapExpanded: boolean = true;
  private _subscriptionIsMobileLayout: Subscription;
  footnotes: string[] = [];
  locations: WebApiModel.Location[];

  settings = SettingsService;

  @ViewChild('reportProblemPopupComponent')
  reportProblemPopupComponent: PopupComponent;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _profileService: ProfileService,
    private _stateService: StateService,
    private _stateRxService: StateRxService,
    private _errorHandlingService: ErrorHandlingService,
    private _uiService: UiService,
    private _footnoteService: FootnoteService,
    private _gaService: GoogleAnalyticsService,
    private _seoService: SeoService,
  ) {}

  ngOnInit(): void {
    // When using the autocomplete dropdown users can select different doctors,
    // ngOnInit event is not fired every time a new person profile is loaded, it only fired the first time profile is loaded,
    // Angular2 is reusing the component.
    // We can use route params observable to bind new profile data.
    // Tried using switchMap but it didn't work well in case if there was an http error,
    // after the error params subscription didn't work anymore.
    // switchMap is supposed to cancel any previous subscriptions and http requests.
    this._route.params.subscribe((params: Params) => {
      this.getData(params);
    });

    // Collapse map on mobile by default
    if (this._uiService.isMobile()) {
      this.mapExpanded = false;
    }

    // If browser is resized from mobile to desktop we need to show the map if it was hidden.
    // We need this becasue of ngIf used to collapse map on mobile.
    // We need ngIf to collapse/expand the map because otherwise it's not initialised properly when it's hidden.
    this._subscriptionIsMobileLayout = this._stateRxService.mobileLayoutChanged.rxSubscribe((isMobile) => {
      // If map is already expanded then keep it that way, it looks like on resize is fired on Android when scrolling
      // probably because the scrollbars are hiding/showing, the map was collapsing when scrolling the page
      this.mapExpanded = !isMobile;
    });
  }

  private getData(params: Params) {
    this._stateService.isComponentLoading(true);
    this._profileService.getPerson(params[RouterHelperService.urlParam.id]).subscribe(
      (response: WebApiModel.Person) => {
        if (response) {
          this._stateService.isComponentLoading(false);
          this.person = null;
          this.person = response;
          this.initialiseVariables();
          this.initialiseMap();
          this._uiService.scrollToTop();
          this._gaService.sendPageViewEvent(this._router.url);
        } else {
          this._errorHandlingService.friendlyErrorHandler(true);
        }
      },
      () => {
        this._errorHandlingService.friendlyErrorHandler(true);
      },
    );
  }

  private initialiseVariables(): void {
    this.name = this.person.firstName + ' ' + this.person.lastName;
    if (this.person.title) {
      this.name = this.person.title + ' ' + this.name;
    }

    // Set page title
    this._seoService.setTitle(this.name);
    this._seoService.setMetaDescription(`${this.name}; ${this.person.specialty || ''}`);

    this.footnotes = this._footnoteService.initPersonFootnotes(this.person);

    // Filter out service categoris that have no matching place so we don't output empty service category headers.

    const filteredPersonServiceCategoriesByPlace: string[] = [];

    // Filter the categories that have some places
    if (this.person.serviceCategoryNames && this.person.serviceCategoryNames.length > 0) {
      /* eslint-disable @typescript-eslint/prefer-for-of */
      for (let i = 0; i < this.person.serviceCategoryNames.length; i++) {
        const serviceCategoryName = this.person.serviceCategoryNames[i];
        if (this.serviceCategoryHasPlace(serviceCategoryName)) {
          filteredPersonServiceCategoriesByPlace.push(serviceCategoryName);
        }
      }
    }

    this.personServiceCategoryNamesByPlace = filteredPersonServiceCategoriesByPlace;
  }

  private serviceCategoryHasPlace(serviceCategoryName: string) {
    if (!serviceCategoryName || !this.person.places) {
      return false;
    }
    /* eslint-disable @typescript-eslint/prefer-for-of */
    for (let i = 0; i < this.person.places.length; i++) {
      const currentPlace = this.person.places[i];

      if (!currentPlace.serviceCategories) {
        return false;
      }
      /* eslint-disable @typescript-eslint/prefer-for-of */
      for (let s = 0; s < currentPlace.serviceCategories.length; s++) {
        const currentPersonServcieCategory = currentPlace.serviceCategories[s];
        if (currentPersonServcieCategory.name === serviceCategoryName) {
          return true;
        }
      }
    }
    return false;
  }

  private initialiseMap(): void {
    let sumLatitude = 0;
    let sumLongitude = 0;
    let isAffiliatedProvider = false;
    let isEasyClaim = false;
    let isTelehealth = false;
    const placesCount = this.person.places.length;
    this.locations = [];
    this.person.places.forEach((place) => {
      sumLatitude += place.latitude;
      sumLongitude += place.longitude;

      if (place.isAffiliatedProvider) {
        isAffiliatedProvider = place.isAffiliatedProvider;
      }
      if (place.isEasyClaim) {
        isEasyClaim = place.isEasyClaim;
      }
      if (place.isTelehealth) {
        isTelehealth = place.isTelehealth;
      }

      const locationDetail = {
        id: place.id,
        practiceName: place.practiceName,
        receptionLocation: place.receptionLocation,
        isAffiliatedProvider: place.isAffiliatedProvider,
        isEasyClaim: place.isEasyClaim,
        webAddress: place.webAddress,
        isConsultant: place.isConsultant,
        isTelehealth: place.isTelehealth,
      } as WebApiModel.LocationDetail;

      const location = {
        latitude: place.latitude,
        longitude: place.longitude,
        address: place.address,
        suburb: place.suburb,
        city: place.city,
        postcode: place.postcode,
        isAffiliatedProvider,
        isEasyClaim,
        isTelehealth,
        locationDetails: [locationDetail],
      } as WebApiModel.Location;
      this.locations.push(location);
    });
    this.mapCenterLatitude = sumLatitude / placesCount;
    this.mapCenterLongitude = sumLongitude / placesCount;
  }

  toggleMap() {
    this.mapExpanded = !this.mapExpanded;
  }

  ngOnDestroy() {
    // prevent memory leak when component destroyed
    this._subscriptionIsMobileLayout.unsubscribe();
  }

  reportProblem() {
    this.reportProblemPopupComponent.openPopup();
  }

  closePopup() {
    this.reportProblemPopupComponent.closePopup();
  }
}
